/**
 * Party Management Page
 * 
 * Unified interface for managing both customers and suppliers through a tabbed layout.
 * Preserves tab state (filters, pagination, search) when switching between tabs.
 * 
 * Requirements: 1.1, 1.2
 * Accessibility: WCAG 2.1 AA compliant with keyboard navigation and ARIA labels
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import {
  People as PeopleIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import CustomersMUI from './CustomersMUI';
import SupplierManagementMUI from './SupplierManagementMUI';
import PageHeader from '../components/ui/PageHeader';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

/**
 * TabPanel component to conditionally render tab content
 * Uses hidden instead of unmounting to preserve component state
 */
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`party-tabpanel-${index}`}
      aria-labelledby={`party-tab-${index}`}
      aria-hidden={value !== index}
      tabIndex={value === index ? 0 : -1}
      {...other}
    >
      {/* Always render children but hide with CSS to preserve state */}
      <Box sx={{ display: value === index ? 'block' : 'none' }}>
        {children}
      </Box>
    </div>
  );
}

/**
 * Generate accessibility props for tabs
 */
function a11yProps(index: number) {
  return {
    id: `party-tab-${index}`,
    'aria-controls': `party-tabpanel-${index}`,
  };
}

interface PartyManagementPageProps {
  initialTab?: 'customers' | 'suppliers';
}

const PartyManagementPage: React.FC<PartyManagementPageProps> = ({ 
  initialTab = 'customers' 
}) => {
  // Tab state - 0 for customers, 1 for suppliers
  const [currentTab, setCurrentTab] = useState(initialTab === 'customers' ? 0 : 1);
  
  // Refs for focus management
  const tabsRef = useRef<HTMLDivElement>(null);
  const previousTabRef = useRef<number>(currentTab);

  /**
   * Handle tab change with focus management
   * State preservation is automatic because we use hidden instead of unmounting
   */
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    previousTabRef.current = currentTab;
    setCurrentTab(newValue);
  };

  /**
   * Manage focus when tab changes
   * Ensures keyboard users can navigate smoothly between tabs
   */
  useEffect(() => {
    // When tab changes, focus should remain on the tab navigation
    // This is handled automatically by MUI Tabs component
    // But we ensure the tabpanel is properly announced to screen readers
    const tabPanel = document.getElementById(`party-tabpanel-${currentTab}`);
    if (tabPanel) {
      // Announce tab change to screen readers
      tabPanel.setAttribute('aria-live', 'polite');
      
      // Remove aria-live after announcement to avoid repeated announcements
      const timer = setTimeout(() => {
        tabPanel.removeAttribute('aria-live');
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [currentTab]);

  /**
   * Handle keyboard navigation
   * Supports Home/End keys for quick navigation
   */
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Home') {
      event.preventDefault();
      setCurrentTab(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setCurrentTab(1);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Party Management"
        subtitle="Manage customers and suppliers from a unified interface"
      />

      <Paper sx={{ width: '100%', borderRadius: 3, overflow: 'hidden' }}>
        {/* Tab Navigation with Brand Gradient */}
        <Box 
          sx={{ 
            background: 'linear-gradient(90deg, #2196f3 0%, #0d47a1 100%)',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
          ref={tabsRef}
        >
          <Tabs 
            value={currentTab} 
            onChange={handleTabChange}
            onKeyDown={handleKeyDown}
            aria-label="Party management tabs - navigate between customers and suppliers"
            sx={{
              '& .MuiTab-root': {
                color: 'rgba(255, 255, 255, 0.7)',
                fontWeight: 500,
                textTransform: 'none',
                fontSize: '0.95rem',
                minHeight: 56,
                '&.Mui-selected': {
                  color: '#ffffff',
                  fontWeight: 600,
                },
                '&:focus-visible': {
                  outline: '2px solid #ffffff',
                  outlineOffset: '-4px',
                  borderRadius: '4px',
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#ffffff',
                height: 3,
              },
            }}
          >
            <Tab
              label="Customers"
              icon={<PeopleIcon />}
              iconPosition="start"
              {...a11yProps(0)}
              aria-label="Customers tab - manage customer accounts and information"
            />
            <Tab
              label="Suppliers"
              icon={<BusinessIcon />}
              iconPosition="start"
              {...a11yProps(1)}
              aria-label="Suppliers tab - manage supplier accounts and vendor relationships"
            />
          </Tabs>
        </Box>

        {/* Customers Tab */}
        <TabPanel value={currentTab} index={0}>
          <CustomersMUI />
        </TabPanel>

        {/* Suppliers Tab */}
        <TabPanel value={currentTab} index={1}>
          <SupplierManagementMUI />
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default PartyManagementPage;
